'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { getBaseImageUrl, getMerchantHref, splitHeading } from '@/constants/hooks'
import { Merchant } from '@/services/dataTypes'
import { ArrowRight, Sparkles, Tag, ChevronRight, Store } from 'lucide-react'

const C = {
    PRIMARY: '#F8F246',
    BROWN: '#8C5826',
    SECONDARY: '#2CBBC3',
    BORDER: '#f0ede8',
    BG: '#fffaf5',
}

interface Props {
    merchants: Merchant[]
    widgetHeading?: string
    widgetText?: string
    mer_slug: string
    mer_slug_type: string
    domain: string
}

const BrandLogoSlider = ({
    merchants,
    widgetHeading = 'Save Big at Top Stores',
    widgetText = 'Discover verified coupons and promotional discounts from our premier store partners.',
    mer_slug,
    mer_slug_type,
    domain,
}: Props) => {
    if (!merchants?.length) return null

    const [first, second] = splitHeading(widgetHeading)

    // Ensure enough items for seamless dual-track infinite loop
    const repeatCount = Math.max(2, Math.ceil(12 / merchants.length))
    const trackItems = Array(repeatCount).fill(merchants).flat()

    return (
        <section className="relative py-10 md:py-14 overflow-hidden" style={{ background: C.BG }}>

            {/* Top & Bottom Accent Lines */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#f0ede8] to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#f0ede8] to-transparent" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">

                {/* ── Section Header ── */}
                <div className="pb-2 mb-2">
                    {/* Row 1: Eyebrow pill + Heading + Button — same line */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
                        <div>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-white border border-[#f0ede8] shadow-2xs mb-2"
                                style={{ color: C.BROWN }}>
                                <Sparkles size={11} className="text-[#FF9934]" />
                                <span>Discounted Stores</span>
                            </div>
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
                                {first} <span style={{ color: C.BROWN }}>{second}</span>
                            </h2>
                        </div>

                        <Link
                            href="/all-stores/A"
                            className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black transition-all duration-200 hover:scale-105 active:scale-95 shadow-xs shrink-0 self-start sm:self-auto"
                            style={{
                                background: C.PRIMARY,
                                color: C.BROWN,
                                boxShadow: '0 2px 8px rgba(248, 242, 70, 0.4)',
                            }}
                        >
                            <span>View All Stores</span>
                            <ChevronRight size={13} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                        </Link>
                    </div>

                    {/* Row 2: Description */}
                    {widgetText && (
                        <p className="text-xs sm:text-sm text-gray-500 font-medium max-w-4xl leading-relaxed">
                            {widgetText}
                        </p>
                    )}
                </div>

                {/* ── Seamless Dual Track Marquee Slider ── */}
                <div className="relative overflow-hidden py-3">

                    {/* Edge Fade Overlays */}
                    <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
                        style={{ background: `linear-gradient(to right, ${C.BG}, transparent)` }} />
                    <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
                        style={{ background: `linear-gradient(to left, ${C.BG}, transparent)` }} />

                    {/* Continuous Dual Synchronized Tracks */}
                    <div className="flex w-max group/marquee">

                        {/* Track 1 */}
                        <div className="flex shrink-0 items-center gap-4 animate-discounted-marquee">
                            {trackItems.map((merchant, i) => {
                                const logoUrl = getBaseImageUrl(domain, merchant.merchant_logo ?? '', '')
                                const totalOffers = (merchant as any)?.total_offers || 0
                                return (
                                    <Link
                                        key={`t1-${i}`}
                                        href={getMerchantHref(merchant, mer_slug, mer_slug_type)}
                                        className="group/card relative flex items-center gap-3.5 p-3 pr-4 rounded-2xl bg-white border border-[#f0ede8] shadow-xs hover:border-[#8C5826]/30 hover:shadow-md hover:-translate-y-1 transition-all duration-300 w-60 shrink-0"
                                    >
                                        {/* Logo Box */}
                                        <div className="w-12 h-12 rounded-xl bg-[#fffbf5] border border-[#f0ede8] p-1.5 flex items-center justify-center shrink-0 group-hover/card:border-[#F8F246] transition-colors">
                                            {merchant.merchant_logo ? (
                                                <Image
                                                    src={logoUrl}
                                                    alt={merchant.merchant_name || 'Store'}
                                                    width={40}
                                                    height={40}
                                                    className="object-contain max-h-9 w-auto transition-transform duration-300 group-hover/card:scale-105"
                                                />
                                            ) : (
                                                <Store size={18} style={{ color: C.BROWN }} />
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="min-w-0 flex-1">
                                            <h4 className="text-xs font-black text-gray-900 group-hover/card:text-[#8C5826] transition-colors truncate">
                                                {merchant.merchant_name}
                                            </h4>
                                            <div className="flex items-center gap-1.5 mt-1">
                                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-[#fffbf0] text-[#8C5826] border border-[#F8F246]/60">
                                                    <Tag size={8} /> {totalOffers > 0 ? `${totalOffers} Deals` : 'Verified'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Hover Arrow */}
                                        <ArrowRight size={13} className="text-gray-300 group-hover/card:text-[#8C5826] group-hover/card:translate-x-0.5 transition-all shrink-0" />
                                    </Link>
                                )
                            })}
                        </div>

                        {/* Track 2 (Clone for infinite seamless loop) */}
                        <div className="flex shrink-0 items-center gap-4 animate-discounted-marquee pl-4" aria-hidden="true">
                            {trackItems.map((merchant, i) => {
                                const logoUrl = getBaseImageUrl(domain, merchant.merchant_logo ?? '', '')
                                const totalOffers = (merchant as any)?.total_offers || 0
                                return (
                                    <Link
                                        key={`t2-${i}`}
                                        href={getMerchantHref(merchant, mer_slug, mer_slug_type)}
                                        className="group/card relative flex items-center gap-3.5 p-3 pr-4 rounded-2xl bg-white border border-[#f0ede8] shadow-xs hover:border-[#8C5826]/30 hover:shadow-md hover:-translate-y-1 transition-all duration-300 w-60 shrink-0"
                                    >
                                        {/* Logo Box */}
                                        <div className="w-12 h-12 rounded-xl bg-[#fffbf5] border border-[#f0ede8] p-1.5 flex items-center justify-center shrink-0 group-hover/card:border-[#F8F246] transition-colors">
                                            {merchant.merchant_logo ? (
                                                <Image
                                                    src={logoUrl}
                                                    alt={merchant.merchant_name || 'Store'}
                                                    width={40}
                                                    height={40}
                                                    className="object-contain max-h-9 w-auto transition-transform duration-300 group-hover/card:scale-105"
                                                />
                                            ) : (
                                                <Store size={18} style={{ color: C.BROWN }} />
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="min-w-0 flex-1">
                                            <h4 className="text-xs font-black text-gray-900 group-hover/card:text-[#8C5826] transition-colors truncate">
                                                {merchant.merchant_name}
                                            </h4>
                                            <div className="flex items-center gap-1.5 mt-1">
                                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-[#fffbf0] text-[#8C5826] border border-[#F8F246]/60">
                                                    <Tag size={8} /> {totalOffers > 0 ? `${totalOffers} Deals` : 'Verified'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Hover Arrow */}
                                        <ArrowRight size={13} className="text-gray-300 group-hover/card:text-[#8C5826] group-hover/card:translate-x-0.5 transition-all shrink-0" />
                                    </Link>
                                )
                            })}
                        </div>

                    </div>
                </div>

            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes discounted-marquee-scroll {
                    0% {
                        transform: translate3d(0, 0, 0);
                    }
                    100% {
                        transform: translate3d(-100%, 0, 0);
                    }
                }
                .animate-discounted-marquee {
                    animation: discounted-marquee-scroll 110s linear infinite;
                    will-change: transform;
                }
                .group\\/marquee:hover .animate-discounted-marquee {
                    animation-play-state: paused;
                }
            `}} />
        </section>
    )
}

export default BrandLogoSlider
